<template>
    <div class="ui-page">
        <ui-page-header title="QGC" subtitle="Quadro geral de credores por caso e por versão do edital.">
            <v-btn outlined color="primary" @click="load(true)">
                <v-icon left small>mdi-microsoft-excel</v-icon>
                Gerar Excel desta aba
            </v-btn>
            <v-btn v-if="$allowed('create')" id="btnNew" type="button" color="primary" depressed to="/qgc/novo">
                <v-icon left small>mdi-plus</v-icon>
                Incluir
            </v-btn>
        </ui-page-header>

        <div class="qgc-tabs-bar">
            <v-tabs v-model="selectedVersion" @change="load(false, true)" background-color="transparent" color="primary" show-arrows class="qgc-tabs-bar__tabs">
                <v-tab v-for="version in qgcVersionTabs" :key="version.id" :value="version.id">
                    {{ version.label }}
                    <ui-badge v-if="version.is_current" tone="success" text="vigente" class="ml-2" />
                    <ui-badge v-if="version.site_visible === false" tone="warning" text="oculto no site" class="ml-1" />
                </v-tab>
            </v-tabs>
            <div class="qgc-tabs-bar__action">
                <!-- Cadastro de multiplos QGCs do caso (referencia legal, historico e comparacao) -->
                <v-btn v-if="dataList.filter.client_id" outlined color="primary" small :loading="versions.loading" @click="openManageVersions">
                    <v-icon left small>mdi-layers-outline</v-icon>
                    QGCs do caso ({{ versions.items.length }})
                </v-btn>
                <span v-else class="ui-muted ui-small">Selecione um caso para gerenciar os QGCs cadastrados</span>
            </div>
        </div>

        <!-- Listas de credores importadas para o caso (antes, menu proprio "Lista de Credores") -->
        <creditor-list-imports v-if="dataList.filter.client_id" :client-id="dataList.filter.client_id" :version="currentTabVersion"
            class="mb-4" :versions="versions.items" @changed="loadCaseVersions(); load(false, true)" />

        <ui-section title="Regras de formação" description="Como cada QGC é gerado.">
            <template #actions>
                <ui-badge tone="primary" :dot="false" text="Caso + CPF/CNPJ + Classe + Moeda" />
            </template>

            <div class="qgc-rules-grid">
                <article v-for="rule in qgcGenerationRules" :key="rule.title" class="ui-card">
                    <div class="ui-card__header">
                        <div class="qgc-rule-title">
                            <v-icon color="primary" size="20">{{ rule.icon }}</v-icon>
                            <h3 class="ui-section-title">{{ rule.title }}</h3>
                        </div>
                    </div>
                    <div class="ui-card__body">
                        <p class="qgc-rule-text">{{ rule.description }}</p>
                        <span class="ui-muted ui-small">{{ rule.source }}</span>
                    </div>
                </article>
            </div>

            <div class="ui-divider"></div>

            <div class="qgc-rules-grid">
                <div>
                    <ui-badge tone="success" text="Aprovado" />
                    <p class="qgc-rule-text ui-muted">Consulta o Edital 52 pela chave do crédito. Se existir, substitui o valor no Edital 7; se não existir, cria um novo crédito.</p>
                </div>

                <div>
                    <ui-badge tone="warning" text="Aprovado parcialmente" />
                    <p class="qgc-rule-text ui-muted">Segue a mesma regra do aprovado, mas grava o valor informado em <strong>partial_amount</strong>.</p>
                </div>

                <div>
                    <ui-badge tone="danger" text="Rejeitado" />
                    <p class="qgc-rule-text ui-muted">Não altera o QGC. O negócio rejeitado é ignorado ao finalizar a habilitação/divergência.</p>
                </div>
            </div>
        </ui-section>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field v-model="dataList.filter.name" placeholder="Buscar pelo nome" prepend-inner-icon="mdi-magnify"
                    dense outlined hide-details class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-text-field v-model="dataList.filter.email" placeholder="E-mail" dense outlined hide-details
                    class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-text-field v-model="dataList.filter.registration" placeholder="CPF/CNPJ" dense outlined hide-details
                    class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-autocomplete v-model="dataList.filter.client_id" :items="dataset.clients" placeholder="Caso"
                    item-value="id" item-text="name" dense outlined hide-details clearable class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" no-data-text="Nenhum caso encontrado" @change="onClientFilterChange(); applyFilters()">
                </v-autocomplete>
                <v-select v-if="clientCompanyFilterOptions.length" v-model="dataList.filter.client_company_id" :items="clientCompanyFilterOptions"
                    placeholder="Todas as empresas do grupo" item-value="id" item-text="name" dense outlined hide-details clearable
                    class="ui-toolbar__field" :menu-props="{ offsetY: true }" no-data-text="Nenhuma empresa encontrada" @change="applyFilters">
                </v-select>
                <!-- Regime de consolidacao cadastrado no caso: substancial = QGC unico do grupo; processual = um QGC por empresa -->
                <v-tooltip v-if="selectedClientConsolidation" bottom>
                    <template #activator="{ on }">
                        <v-chip v-on="on" small outlined color="primary" class="ml-1">
                            <v-icon left x-small>mdi-domain</v-icon>{{ selectedClientConsolidation.text }}
                        </v-chip>
                    </template>
                    <span>{{ selectedClientConsolidation.hint }}</span>
                </v-tooltip>
            </template>

            <!-- Totalizador: soma no banco de todos os registros que batem com os filtros e a aba, por moeda -->
            <div v-if="dataList.totals.length" class="qgc-totals">
                <span class="ui-muted ui-small">
                    Totalizador dos filtros aplicados
                    <v-tooltip bottom>
                        <template #activator="{ on }">
                            <v-icon v-on="on" x-small class="ml-1 ui-cell-chevron">mdi-information-outline</v-icon>
                        </template>
                        <span>Soma de todos os registros da aba e dos filtros atuais, não apenas da página exibida. Moedas diferentes não são somadas entre si.</span>
                    </v-tooltip>
                </span>
                <span v-for="item in dataList.totals" :key="`total-${item.currency}`" class="qgc-totals__item">
                    <span class="ui-muted">{{ currencyLabel(item.currency) }}</span>
                    <strong class="qgc-totals__value">{{ formatTotalAmount(item.total) }}</strong>
                    <span class="ui-muted ui-small">{{ item.count }} {{ item.count === 1 ? 'registro' : 'registros' }}</span>
                </span>
            </div>

            <v-data-table :headers="dataList.headers" :items="dataList.data"
                :server-items-length="dataList.count" :options.sync="dataList.options"
                @update:options="load()" disable-sort hide-default-footer :loading="dataList.loading"
                no-data-text="Nenhum registro encontrado nesta aba" loading-text="Carregando dados..."
                class="ui-clickable-rows" @click:row="openRow">

                <template v-slot:[`item.name`]="{ item }">
                    <div class="d-flex align-center">
                        <v-avatar :size="30" class="mr-3">
                            <img :src="'https://ui-avatars.com/api/?name=' + item.name" />
                        </v-avatar>
                        <div>
                            <div class="ui-cell-main">{{ item.name }}</div>
                            <div v-if="item.email" class="ui-cell-sub">{{ item.email }}</div>
                        </div>
                    </div>
                </template>

                <template v-slot:[`item.client.name`]="{ item }">
                    {{ truncateText(item.client ? item.client.name : 'N/A', 30) }}
                </template>

                <template v-slot:[`item.client_company.name`]="{ item }">
                    {{ item.client_company ? truncateText(item.client_company.name, 30) : 'Principal' }}
                </template>

                <template v-slot:[`item.registration`]="{ item }">
                    <span class="ui-nowrap">{{ formatCpfCnpj(item.registration) }}</span>
                    <v-tooltip v-if="item.registration_fictitious" bottom>
                        <template #activator="{ on }">
                            <v-icon v-on="on" small color="warning" class="ml-1">mdi-alert-circle</v-icon>
                        </template>
                        <span>CPF/CNPJ fictício: documento desconhecido na importação. Regularize antes de pagamentos.</span>
                    </v-tooltip>
                </template>

                <template v-slot:[`item.type`]="{ item }">
                    <ui-badge color="primary" :text="getTypeName(item.type, item.client_id)" />
                </template>

                <template v-slot:[`item.source_description`]="{ item }">
                    <div class="source-cell">
                        <ui-badge :color="getSourceColor(item.source_type)" :text="getSourceLabel(item.source_type)" />
                        <span class="ui-cell-sub">{{ item.source_description || getSourceLabel(item.source_type) }}</span>
                    </div>
                </template>

                <template v-slot:[`item.amount`]="{ item }">
                    <span class="ui-nowrap">R$ {{ item.amount | currency }}</span>
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                    {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}
                </template>

                <template v-slot:[`item.updated_at`]="{ item }">
                    <span v-if="item.updated_at && item.updated_at !== item.created_at">{{ item.updated_at | defaultDate("DD/MM/YYYY HH:mm") }}</span>
                    <span v-else class="ui-muted">—</span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Editar', icon: 'mdi-pencil-outline', disabled: !$check('Qgc', 'PATCH'), handler: () => viewItem(item) },
                        { text: 'Excluir', icon: 'mdi-delete-outline', danger: true, handler: () => deleteItem(item) , disabled: !$allowed('delete') },
                    ]" />
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.itemsPerPage"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>

        <v-dialog v-model="deleteDialog" max-width="400px">
            <v-card class="ui-card" flat>
                <div class="ui-card__header">
                    <h3 class="ui-section-title">Aviso</h3>
                </div>

                <div class="ui-card__body">
                    Tem certeza que deseja excluir o registro de
                    <strong v-if="itemToDelete">
                        {{ itemToDelete.name }}
                    </strong>?
                </div>

                <div class="ui-card__footer">
                    <v-btn text @click="deleteDialog = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('delete')" color="error" depressed @click="confirmDelete" :loading="dataList.loading">Excluir</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- QGCs cadastrados do caso -->
        <v-dialog v-model="versions.dialog" max-width="1100px" scrollable>
            <v-card>
                <v-card-title>
                    <v-icon left color="primary">mdi-layers-outline</v-icon>
                    QGCs do caso <span v-if="selectedClientFilter" class="ml-2 grey--text text-subtitle-1">— {{ selectedClientFilter.name }}</span>
                    <v-spacer />
                    <v-btn v-if="$allowed('create')" color="primary" depressed small @click="openVersionForm()">
                        <v-icon left small>mdi-plus</v-icon> Novo QGC
                    </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text style="max-height: 70vh;">
                    <v-alert dense text type="info" class="mt-4">
                        Cada QGC é uma versão completa do quadro. As versões 1 a 3 são as etapas geradas pelo sistema
                        (Edital 52 importado, Edital 7 das hab/div e Consolidado). Novos QGCs recebem o próximo número e a
                        <strong>versão de maior número é a vigente</strong> (usada na área do credor, nas AGCs e no site).
                        Um novo QGC pode nascer como cópia de outro (para registrar alterações do art. 8º, por exemplo) ou vazio,
                        para receber uma lista de credores importada.
                    </v-alert>
                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th>Versão</th>
                                <th>Referência</th>
                                <th>Título</th>
                                <th>Data</th>
                                <th>Documento</th>
                                <th>Origem</th>
                                <th class="text-right">Credores</th>
                                <th class="text-right">Total (R$)</th>
                                <th>Cadastro</th>
                                <th class="text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in versions.items" :key="item.id">
                                <td>
                                    <strong>v{{ item.version }}</strong>
                                    <ui-badge v-if="item.is_current" tone="success" text="vigente" class="ml-1" />
                                    <v-tooltip v-if="item.site_visible === false" bottom max-width="320">
                                        <template #activator="{ on }">
                                            <span v-on="on"><ui-badge tone="warning" text="oculto no site" class="ml-1" /></span>
                                        </template>
                                        <span>O site só mostra o Edital do art. 7º (e os QGCs seguintes) a partir da data de publicação. Preencha a "Data do edital / decisão" desta versão em Editar.</span>
                                    </v-tooltip>
                                </td>
                                <td>{{ item.reference_label }}</td>
                                <td>
                                    {{ item.title }}
                                    <div v-if="item.description" class="text-caption grey--text">{{ item.description }}</div>
                                </td>
                                <td>{{ item.reference_date ? item.reference_date.split('-').reverse().join('/') : '—' }}</td>
                                <td>
                                    <span v-if="item.reference_document">{{ item.reference_document }}</span>
                                    <a v-if="item.file_url" :href="item.file_url" target="_blank" class="ml-1">
                                        <v-icon small color="primary">mdi-file-pdf-box</v-icon>
                                    </a>
                                    <span v-if="!item.reference_document && !item.file_url">—</span>
                                </td>
                                <td>
                                    {{ versionOriginLabel(item) }}
                                    <span v-if="item.base_version" class="text-caption grey--text">(de v{{ item.base_version }})</span>
                                </td>
                                <td class="text-right">{{ item.creditors_count }}<span class="text-caption grey--text"> / {{ item.distinct_creditors }} CPF/CNPJ</span></td>
                                <td class="text-right">{{ formatTotalAmount(item.total_amount) }}</td>
                                <td class="text-caption">
                                    {{ item.created_at | defaultDate('DD/MM/YYYY') }}
                                    <span v-if="item.created_by_name">· {{ item.created_by_name }}</span>
                                </td>
                                <td class="text-right text-no-wrap">
                                    <v-btn icon small title="Ver créditos" @click="showVersionTab(item)"><v-icon small>mdi-table-eye</v-icon></v-btn>
                                    <v-btn icon small title="Comparar com a versão anterior" :disabled="!previousVersionOf(item)" @click="compareWithPrevious(item)"><v-icon small>mdi-compare-horizontal</v-icon></v-btn>
                                    <v-btn v-if="$allowed('edit')" icon small title="Editar" @click="openVersionForm(item)"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
                                    <v-btn v-if="$allowed('delete')" icon small title="Excluir" :disabled="item.is_structural" @click="deleteVersion(item)"><v-icon small color="error">mdi-delete-outline</v-icon></v-btn>
                                </td>
                            </tr>
                            <tr v-if="!versions.items.length">
                                <td colspan="10" class="text-center grey--text py-4">Nenhum QGC cadastrado para este caso.</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="versions.dialog = false">Fechar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Novo / editar QGC -->
        <v-dialog v-model="versions.formDialog" max-width="720px" persistent>
            <v-card>
                <v-card-title>{{ versions.form.id ? `Editar QGC v${versions.form.version}` : 'Novo QGC' }}</v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                    <v-form ref="versionForm" class="mt-4">
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-select v-model="versions.form.reference_type" :items="versions.referenceTypes" item-value="id" item-text="label"
                                    label="Referência legal" outlined dense hide-details="auto" :rules="[$rules.required]" />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="versions.form.reference_date" label="Data do edital / decisão" type="date" outlined dense hide-details="auto" clearable />
                            </v-col>
                            <v-col cols="12">
                                <v-text-field v-model="versions.form.title" label="Título" outlined dense hide-details="auto" :rules="[$rules.required]"
                                    hint="Ex.: Edital art. 7º, § 2º — publicado em 10/09/2026; QGC após decisão das impugnações (art. 8º)" persistent-hint />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="versions.form.reference_document" label="Documento de referência" outlined dense hide-details="auto"
                                    hint="Nº do edital, evento do processo, DJE…" persistent-hint />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-file-input v-model="versions.form.file" label="Arquivo (PDF do edital/decisão)" accept=".pdf,image/*" outlined dense hide-details="auto" prepend-icon="" clearable />
                                <a v-if="versions.form.file_url" :href="versions.form.file_url" target="_blank" class="text-caption">Ver arquivo atual</a>
                            </v-col>
                            <v-col cols="12">
                                <v-textarea v-model="versions.form.description" label="Descrição / observações" outlined dense rows="2" hide-details="auto" />
                            </v-col>
                            <template v-if="!versions.form.id">
                                <v-col cols="12" sm="6">
                                    <v-select v-model="versions.form.base_version" :items="baseVersionOptions" item-value="id" item-text="label"
                                        label="Créditos iniciais" outlined dense hide-details="auto" />
                                </v-col>
                                <v-col cols="12" sm="6" class="d-flex align-center text-caption grey--text">
                                    <span v-if="versions.form.base_version">Todos os créditos ativos da versão escolhida serão copiados para o novo QGC; depois é só editar o que mudou.</span>
                                    <span v-else>O QGC nasce vazio: importe uma lista de credores escolhendo esta versão como destino, ou inclua os créditos manualmente.</span>
                                </v-col>
                            </template>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="versions.formDialog = false" :disabled="versions.saving">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" depressed @click="saveVersion" :loading="versions.saving">{{ versions.form.id ? 'Salvar' : 'Criar QGC' }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Comparacao entre versoes -->
        <v-dialog v-model="versions.compareDialog" max-width="1100px" scrollable>
            <v-card v-if="versions.compare">
                <v-card-title>
                    Comparação: v{{ versions.compare.from.version }} → v{{ versions.compare.to.version }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text style="max-height: 70vh;">
                    <v-row class="mt-1">
                        <v-col cols="6" sm="3"><div class="text-caption grey--text">v{{ versions.compare.from.version }}</div><strong>{{ versions.compare.from.count }}</strong> créditos · R$ {{ formatTotalAmount(versions.compare.from.total) }}</v-col>
                        <v-col cols="6" sm="3"><div class="text-caption grey--text">v{{ versions.compare.to.version }}</div><strong>{{ versions.compare.to.count }}</strong> créditos · R$ {{ formatTotalAmount(versions.compare.to.total) }}</v-col>
                        <v-col cols="12" sm="6" class="d-flex flex-wrap align-center" style="gap: 6px;">
                            <ui-badge tone="success" :text="`${versions.compare.summary.added} incluído(s)`" />
                            <ui-badge tone="danger" :text="`${versions.compare.summary.removed} removido(s)`" />
                            <ui-badge tone="warning" :text="`${versions.compare.summary.changed} valor alterado`" />
                            <ui-badge tone="neutral" :text="`${versions.compare.summary.unchanged} sem alteração`" />
                        </v-col>
                    </v-row>
                    <template v-for="block in compareBlocks">
                        <div :key="block.key + '-t'" class="font-weight-bold mt-4 mb-1">{{ block.title }} ({{ block.rows.length }})</div>
                        <v-simple-table :key="block.key" dense v-if="block.rows.length">
                            <thead><tr><th>Credor</th><th>CPF/CNPJ</th><th>Classe</th><th class="text-right">Antes</th><th class="text-right">Depois</th><th class="text-right">Diferença</th></tr></thead>
                            <tbody>
                                <tr v-for="row in block.rows" :key="block.key + row.id">
                                    <td>{{ row.name }}</td>
                                    <td>{{ row.registration }}</td>
                                    <td>{{ row.type | enum(dataset.creditorTypes, 'id', 'name') }}</td>
                                    <td class="text-right">{{ row.amount_before === null ? '—' : formatTotalAmount(row.amount_before) }}</td>
                                    <td class="text-right">{{ row.amount_after === null ? '—' : formatTotalAmount(row.amount_after) }}</td>
                                    <td class="text-right" :class="row.difference > 0 ? 'success--text' : (row.difference < 0 ? 'error--text' : '')">{{ row.difference === null ? '—' : formatTotalAmount(row.difference) }}</td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                        <div :key="block.key + '-e'" v-else class="text-caption grey--text">Nenhum.</div>
                    </template>
                    <div v-if="compareTruncated" class="text-caption grey--text mt-3">Listas limitadas a {{ versions.compare.list_limit }} linhas por bloco.</div>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="versions.compareDialog = false">Fechar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </div>
</template>

<script>
import axios from 'axios';
import _ from 'lodash';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import creditorTypes from "@/common/data/creditor-types.data";
import qgcVersions from "@/common/data/qgc-versions.data"; 
import creditorClassUtil from "@/common/util/creditor-class.util";
import CreditorListImports from "@/components/CreditorListImports.vue";

export default {
    name: "QgcList",

    data: () => ({
        deleteDialog: false,
        itemToDelete: null,
        
        qgcVersions: qgcVersions,
        selectedVersion: 0,
        // QGCs cadastrados do caso selecionado (abas dinamicas, gestao e comparacao)
        versions: {
            items: [],
            referenceTypes: [],
            loading: false,
            dialog: false,
            formDialog: false,
            compareDialog: false,
            compare: null,
            saving: false,
            form: { id: null, version: null, reference_type: 'other', title: '', description: '', reference_date: null, reference_document: '', file: null, file_url: null, base_version: null },
        },
        qgcGenerationRules: [
            {
                icon: "mdi-file-import-outline",
                title: "Edital 52, § 1º",
                description: "Lista original importada na plataforma, enviada pela empresa recuperanda.",
                source: "Base inicial do QGC"
            },
            {
                icon: "mdi-file-sync-outline",
                title: "Edital 7º, § 2º",
                description: "Lista gerada a partir das habilitações e divergências aprovadas ou aprovadas parcialmente.",
                source: "Resultado da análise administrativa"
            },
            {
                icon: "mdi-file-check-outline",
                title: "QGC Consolidado",
                description: "Lista gerada após novas alterações, impugnações judiciais e consolidação final do caso.",
                source: "Base final consolidada"
            }
        ],

        dataset: {
            clients: [],
            creditorTypes: creditorTypes,
            classesByClient: {}
        },

        dataList: {
            // Totalizador dos filtros aplicados, por moeda: [{ currency, total, count }]
            totals: [],
            filter: {
                name: "",
                client_id: "",
                client_company_id: "",
                email: "",
                registration: ""
            },
            headers: [
                { text: "Nome", value: "name" },
                { text: "CPF/CNPJ", value: "registration" },
                { text: "Caso", value: "client.name" },
                { text: "Empresa", value: "client_company.name" },
                { text: "Classe", value: "type" },
                { text: "Valor", value: "amount" },
                { text: "Origem", value: "source_description" },
                { text: "Criado em", value: "created_at" },
                { text: "Atualizado em", value: "updated_at" },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
            ],
            data: [],
            loading: false,
            count: 0,
            options: {
                page: 1,
                itemsPerPage: 10,
            },
            footerOptions: {
                itemsPerPageText: 'Itens por página:',
                itemsPerPageOptions: [10, 20, 30, 40, 50],
                pageText: '{0}-{1} de {2}',
            }
        },
    }),

    components: { CreditorListImports },

    created() {
        this.loadClients();
        this.loadCreditorClassLabels();
        // Caso vindo pela URL (ex.: volta da importacao de lista de credores).
        if (this.$route.query.client_id) {
            this.dataList.filter.client_id = Number(this.$route.query.client_id);
            this.$nextTick(() => this.load(false, true));
        }
    },

    watch: {
        'dataList.filter.client_id'(value, oldValue) {
            if (String(value) === String(oldValue)) return;

            this.loadCreditorClassLabels();
            this.selectedVersion = 0;
            this.loadCaseVersions();
        },
    },

    computed: {
        hasFilters() {
            return Object.values(this.dataList.filter).some((value) => value);
        },

        // Versao da aba aberta (destino padrao de uma nova importacao).
        currentTabVersion() {
            const tab = this.qgcVersionTabs[this.selectedVersion];
            return tab ? tab.id : null;
        },

        // Com caso selecionado, as abas sao os QGCs cadastrados; sem caso, as 3 etapas fixas.
        qgcVersionTabs() {
            if (this.dataList.filter.client_id && this.versions.items.length) {
                return this.versions.items.map((item) => ({
                    id: item.version,
                    label: `v${item.version} · ${item.title}`,
                    is_current: item.is_current,
                    site_visible: item.site_visible,
                }));
            }
            return this.qgcVersions.map((item) => ({ ...item, is_current: false }));
        },

        baseVersionOptions() {
            return [{ id: null, label: 'Nenhum (QGC vazio)' }].concat(
                this.versions.items.map((item) => ({ id: item.version, label: `Copiar de v${item.version} · ${item.title} (${item.creditors_count} créditos)` }))
            );
        },

        compareBlocks() {
            const c = this.versions.compare;
            if (!c) return [];
            return [
                { key: 'added', title: 'Incluídos', rows: c.added },
                { key: 'removed', title: 'Removidos', rows: c.removed },
                { key: 'changed', title: 'Valor alterado', rows: c.changed },
            ];
        },

        compareTruncated() {
            const c = this.versions.compare;
            return !!c && (c.summary.added > c.added.length || c.summary.removed > c.removed.length || c.summary.changed > c.changed.length);
        },

        selectedClientFilter() {
            return this.dataset.clients.find(client => Number(client.id) === Number(this.dataList.filter.client_id)) || null;
        },

        // Regime do caso filtrado (clients.consolidation_type).
        selectedClientConsolidation() {
            const client = this.selectedClientFilter;
            const type = client ? Number(client.consolidation_type) : 0;
            if (type === 2) return { text: 'Consolidação substancial', hint: 'QGC único do grupo (art. 69-J): a empresa devedora é só informativa.' };
            if (type === 1) return { text: 'Consolidação processual', hint: 'Um QGC por empresa (art. 69-G): use o filtro de empresa do grupo.' };
            return null;
        },

        clientCompanyFilterOptions() {
            const companies = this.selectedClientFilter && Array.isArray(this.selectedClientFilter.companies)
                ? this.selectedClientFilter.companies
                : [];

            return companies
                .filter(company => company && company.name)
                .map(company => ({ id: company.id, name: company.name }));
        },
    },

    methods: {
        async loadCreditorClassLabels() {
            // Rotulo de tabela e exportacao: inclui classes ocultas, para que
            // credores ja classificados nelas nao apareçam como "N/A".
            this.dataset.creditorTypes = await creditorClassUtil.listLabels(this.dataList.filter.client_id);
        },

        // A listagem cruza varios casos e o id da classe so tem sentido dentro
        // do caso (falencias tem classes proprias). Carrega as classes de cada
        // caso presente nas linhas para nomear a coluna "Classe" e a exportacao.
        async ensureClassLabelsForRows(rows) {
            const clientIds = [...new Set((rows || []).map(r => Number(r.client_id)).filter(Boolean))]
                .filter(id => !this.dataset.classesByClient[id]);
            if (!clientIds.length) return;

            const results = await Promise.all(
                clientIds.map(id => creditorClassUtil.listByClient(id, { includeInactive: true }))
            );

            const byClient = { ...this.dataset.classesByClient };
            clientIds.forEach((id, index) => { byClient[id] = results[index] || []; });
            this.dataset.classesByClient = byClient;
        },

        getTypeName(typeId, clientId = null) {
            const classes = clientId && this.dataset.classesByClient[Number(clientId)];
            const fromClient = classes && classes.find(s => Number(s.id) === Number(typeId));
            if (fromClient && fromClient.name) return fromClient.name;

            const type = this.dataset.creditorTypes.find(s => Number(s.id) === Number(typeId));
            return type && type.name ? type.name : 'N/A';
        },

        getSourceLabel(sourceType) {
            const labels = {
                import: "Importação",
                creditor_list: "Lista de credores",
                edital_52: "Edital 52",
                edital_7: "Edital 7",
                user_event: "Hab/Div",
                user_event_exclusion: "Exclusão (Hab/Div)",
                chc: "CHC",
                official_letter: "Análise de Ofício",
                official_letter_exclusion: "Exclusão (Ofício)",
                attorney_fee: "Honorário",
                judicial_challenge: "Impugnação",
                judicial_challenge_sentence: "Impugnação com sentença",
                manual: "Manual",
                general_meeting_scenario: "Cenário da AGC",
            };

            return labels[sourceType] || "Manual";
        },

        getSourceColor(sourceType) {
            const colors = {
                attorney_fee: "warning",
                user_event: "success",
                user_event_exclusion: "error",
                chc: "teal",
                official_letter: "deep-purple",
                official_letter_exclusion: "error",
                edital_52: "info",
                edital_7: "primary",
                import: "grey",
                creditor_list: "info",
                judicial_challenge: "purple",
                judicial_challenge_sentence: "purple",
            };

            return colors[sourceType] || "grey";
        },

        truncateText(text, maxLength) {
            if (!text) return "";
            if (text.length > maxLength) {
                return text.slice(0, maxLength) + '...';
            }
            return text;
        },

        formatCpfCnpj(value) {
            if (!value) return "";
            const rawValue = String(value).replace(/\D/g, ''); 
            return rawValue.length === 14 ? cnpj.format(rawValue) : cpf.format(rawValue);
        },

        currencyLabel(currency) {
            return ({ 1: 'BRL', 2: 'USD', 3: 'EUR' })[Number(currency)] || `Moeda ${currency}`;
        },

        formatTotalAmount(value) {
            return Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        getRealVersionId() {
            const versionData = this.qgcVersionTabs[this.selectedVersion];
            return versionData ? versionData.id : null;
        },

        // ---- QGCs cadastrados do caso ----
        async loadCaseVersions() {
            const clientId = this.dataList.filter.client_id;
            if (!clientId) { this.versions.items = []; return; }
            this.versions.loading = true;
            try {
                const result = await axios.get(`/api/admin/clients/${clientId}/qgc-versions`);
                const content = result.data.content || {};
                this.versions.items = content.versions || [];
                this.versions.referenceTypes = content.reference_types || [];
            } catch (error) {
                this.versions.items = [];
                this.$dialog.notify.error(this.$codes(error) || 'Erro ao carregar os QGCs do caso', { position: 'top-right', timeout: 4000 });
            } finally {
                this.versions.loading = false;
            }
        },

        async openManageVersions() {
            await this.loadCaseVersions();
            this.versions.dialog = true;
        },

        versionOriginLabel(item) {
            return ({ legacy: 'Etapa do sistema', copy: 'Cópia', manual: 'Cadastro manual', import: 'Importação' })[item.origin] || item.origin || '—';
        },

        previousVersionOf(item) {
            const older = this.versions.items.filter((v) => Number(v.version) < Number(item.version));
            return older.length ? older[older.length - 1] : null;
        },

        showVersionTab(item) {
            const index = this.qgcVersionTabs.findIndex((tab) => Number(tab.id) === Number(item.version));
            if (index >= 0) {
                this.selectedVersion = index;
                this.versions.dialog = false;
                this.load(false, true);
            }
        },

        openVersionForm(item = null) {
            this.versions.form = item
                ? { id: item.id, version: item.version, reference_type: item.reference_type, title: item.title, description: item.description || '', reference_date: item.reference_date || null, reference_document: item.reference_document || '', file: null, file_url: item.file_url || null, base_version: null }
                : { id: null, version: null, reference_type: 'other', title: '', description: '', reference_date: null, reference_document: '', file: null, file_url: null, base_version: this.versions.items.length ? this.versions.items[this.versions.items.length - 1].version : null };
            this.versions.formDialog = true;
        },

        async saveVersion() {
            if (this.$refs.versionForm && !this.$refs.versionForm.validate()) return;
            const form = this.versions.form;
            const fd = new FormData();
            fd.append('reference_type', form.reference_type);
            fd.append('title', form.title);
            fd.append('description', form.description || '');
            fd.append('reference_date', form.reference_date || '');
            fd.append('reference_document', form.reference_document || '');
            if (form.file) fd.append('file', Array.isArray(form.file) ? form.file[0] : form.file);
            if (!form.id && form.base_version) { fd.append('base_version', form.base_version); fd.append('copy_base', '1'); }

            this.versions.saving = true;
            try {
                let created = null;
                if (form.id) {
                    await axios.patch(`/api/admin/qgc-versions/${form.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    this.$dialog.notify.success('QGC atualizado.', { position: 'top-right', timeout: 3000 });
                } else {
                    const result = await axios.post(`/api/admin/clients/${this.dataList.filter.client_id}/qgc-versions`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    created = result.data.content;
                    this.$dialog.notify.success(`QGC v${created.version} criado${created.copied ? ` com ${created.copied} crédito(s) copiado(s)` : ''}.`, { position: 'top-right', timeout: 4000 });
                }
                this.versions.formDialog = false;
                await this.loadCaseVersions();
                if (created) this.showVersionTab(created);
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error) || 'Erro ao salvar o QGC', { position: 'top-right', timeout: 5000 });
            } finally {
                this.versions.saving = false;
            }
        },

        async deleteVersion(item) {
            const proceed = await this.$dialog.confirm({
                title: `Excluir QGC v${item.version}`,
                text: `Os ${item.creditors_count} crédito(s) desta versão serão excluídos e a versão anterior volta a ser a vigente. Confirmar?`,
                actions: { false: 'Cancelar', true: 'Excluir' },
            });
            if (!proceed) return;
            try {
                await axios.delete(`/api/admin/qgc-versions/${item.id}`);
                this.$dialog.notify.success('QGC excluído.', { position: 'top-right', timeout: 3000 });
                this.selectedVersion = 0;
                await this.loadCaseVersions();
                this.load(false, true);
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error) || 'Erro ao excluir o QGC', { position: 'top-right', timeout: 5000 });
            }
        },

        async compareWithPrevious(item) {
            const previous = this.previousVersionOf(item);
            if (!previous) return;
            try {
                const result = await axios.get(`/api/admin/clients/${this.dataList.filter.client_id}/qgc-versions/compare`, { params: { from: previous.version, to: item.version } });
                this.versions.compare = result.data.content;
                this.versions.compareDialog = true;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error) || 'Erro ao comparar as versões', { position: 'top-right', timeout: 5000 });
            }
        },

        applyFilters() {
            this.load(false, true);
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        clearFilters() {
            this.dataList.filter = { name: "", client_id: "", client_company_id: "", email: "", registration: "" };
            this.applyFilters();
        },

        // A tabela recarrega sozinha pelo update:options ao mudar pagina/tamanho.
        pageChanged(page) {
            this.dataList.options.page = page;
        },

        setPageSize(size) {
            this.dataList.options.itemsPerPage = size;
        },

        // A linha abre o detalhe so com a mesma permissao do "Editar".
        openRow(item) {
            if (this.$check('Qgc', 'GET')) this.viewItem(item);
        },

        onClientFilterChange() {
            this.dataList.filter.client_company_id = "";
        },

        async load(exportCsv = false, resetPage = false) {
            if (resetPage) {
                this.dataList.options.page = 1;
            }

            let searchPayload = Object.fromEntries(
                Object.entries(this.dataList.filter).filter(([_, v]) => v !== null && v !== undefined && v !== "")
            );

            searchPayload.version = this.getRealVersionId();

            const searchString = Object.keys(searchPayload).length > 0 ? JSON.stringify(searchPayload) : null;

            this.dataList.loading = true;

            const limit = exportCsv ? 1000000 : (this.dataList.options.itemsPerPage || 10);
            const start = exportCsv ? 1 : this.dataList.options.page;
            
            try {
                const result = await axios.get('/api/admin/users-creditors', { 
                    params: { page: start, limit: limit, search: searchString } 
                });

                const responsePayload = result.data.data || result.data.content; 
                const rows = responsePayload.data || [];

                await this.ensureClassLabelsForRows(rows);

                if (!exportCsv) {
                    this.dataList.data = rows.map(r => {
                        const foundClient = this.dataset.clients.find(c => c.id === r.client_id);
                        return {
                            ...r,
                            client: r.client || { name: foundClient ? foundClient.name : 'N/A' },
                            client_company: r.client_company || r.clientCompany || null
                        };
                    });
                    
                    this.dataList.count = responsePayload.paging ? responsePayload.paging.total : (responsePayload.total || 0);
                    this.dataList.totals = Array.isArray(responsePayload.totals) ? responsePayload.totals : [];
                    return;
                }

                this.generateCSV(rows);

            } catch (error) {
                console.error('error ', error);
                this.$dialog.notify.error(this.$codes(error) || "Erro ao buscar dados", { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }
        },

        async loadClients() {
            try {
                const result = await axios.get(`/api/clients`, { params: { start: 1, limit: 100000, search: "{}" } });
                
                const data = result.data.data ? result.data.data.data : result.data.content.data;
                
                if (data) {
                    data.sort((a, b) => a.name.localeCompare(b.name));
                    this.dataset.clients = [{ id: "", name: "Todos " }, ...data];
                }

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 2000 });
            }
        },

        viewItem(item) {
            this.$router.push(`qgc/${item.id}`);
        },

        deleteItem(item) {
            this.itemToDelete = item;
            this.deleteDialog = true;
        },

        async confirmDelete() {
            if (!this.itemToDelete) return;

            this.dataList.loading = true;

            try {
                await axios.delete(`/api/admin/users-creditors/${this.itemToDelete.id}`);

                this.$dialog.notify.success('Registro excluído com sucesso!', { position: 'top-right', timeout: 3000 });
                this.deleteDialog = false;
                this.itemToDelete = null;
                this.load();

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error) || 'Erro ao excluir', { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }
        },

        // Exporta a aba em Excel (.xlsx): acentos preservados e valor como numero.
        async generateCSV(data) {
            const proceed = await this.$dialog.confirm({
                text: 'Deseja exportar os dados desta aba para Excel?',
                title: 'Exportar dados',
                actions: { false: 'Cancelar', true: 'Exportar' }
            });

            if (!proceed) return;
            const asXlsx = true;

            if (!data || data.length === 0) {
                this.$dialog.notify.error("Sem dados para exportar nesta versão!", { position: "top-right", timeout: 3000 });
                return;
            }

            const formatDate = (value) => (value ? this.$options.filters.defaultDate(value, "DD/MM/YYYY HH:mm:ss") : "");

            const newData = data.map(obj => {
                const foundClient = this.dataset.clients.find(c => c.id === obj.client_id);
                const clientName = obj.client ? obj.client.name : (foundClient ? foundClient.name : "N/A");
                const hasAmount = obj.amount || obj.amount === 0;

                return {
                    "NOME CREDOR": obj.name,
                    "CPF/CNPJ": this.formatCpfCnpj(obj.registration),
                    "E-MAIL": obj.email || "N/A",
                    "CASO": clientName,
                    "EMPRESA DO GRUPO": obj.client_company ? obj.client_company.name : (obj.clientCompany ? obj.clientCompany.name : "Principal"),
                    "VALOR": hasAmount
                        ? (asXlsx ? Number(obj.amount) : Number(obj.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }))
                        : "N/A",
                    "MOEDA": this.currencyLabel ? this.currencyLabel(obj.currency) : (obj.currency || 1),
                    "CLASSE": this.getTypeName(obj.type, obj.client_id),
                    "ORIGEM": obj.source_description || this.getSourceLabel(obj.source_type),
                    "CRIADO EM": formatDate(obj.created_at),
                    "ATUALIZADO EM": obj.updated_at && obj.updated_at !== obj.created_at ? formatDate(obj.updated_at) : "",
                };
            });

            const tabNameForFile = this.qgcVersions[this.selectedVersion].label.replace(/,/g, '').replace(/ /g, '_');

            {
                try {
                    const XLSX = await import('xlsx');
                    const worksheet = XLSX.utils.json_to_sheet(newData);
                    worksheet['!cols'] = Object.keys(newData[0]).map((key) => ({ wch: Math.min(60, Math.max(key.length + 2, ...newData.slice(0, 200).map((row) => String(row[key] === null || row[key] === undefined ? '' : row[key]).length + 2))) }));
                    const workbook = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'QGC');
                    XLSX.writeFile(workbook, `QGC_${tabNameForFile}.xlsx`);
                } catch (error) {
                    console.error('error ', error);
                    this.$dialog.notify.error("Erro ao gerar o arquivo XLSX", { position: 'top-right', timeout: 5000 });
                }
            }

        },
    },
};
</script>


<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.qgc-tabs-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 16px;
    margin-bottom: 16px;
}

.qgc-tabs-bar__tabs {
    flex: 1 1 480px;
    min-width: 0;
}

.qgc-tabs-bar__action {
    flex: 0 0 auto;
    margin-left: auto;
}

.qgc-rules-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
}

// Os cartoes internos ficam em grade: sem o espacamento vertical entre cartoes
.qgc-rules-grid > .ui-card + .ui-card {
    margin-top: 0;
}

.qgc-rule-title {
    display: flex;
    align-items: center;
    gap: 8px;
}

.qgc-rule-text {
    font-size: 14px;
    line-height: 1.5;
    margin: 8px 0;
}

.qgc-totals {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 8px 28px;
    padding: 10px 16px;
    border-bottom: 1px solid $ui-border;
    background: $ui-bg-muted;
    font-size: 14px;
}

.qgc-totals__item {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
}

.qgc-totals__value {
    font-weight: 600;
    color: $ui-fg;
}

.source-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 180px;
}

@media (max-width: 960px) {
    .qgc-rules-grid {
        grid-template-columns: 1fr;
    }
}
</style>
